import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import TableCard from '@/components/Tables/TableCard/TableCard';
import { MenuDetails, Product } from '@/types/menus.types';
import rowStyles from './TableRow.module.css';
import { CellEditProduct } from './CellEditProduct';
import { CellEditProductPrice } from './CellEditProductPrice';
import { IconTooltipCell } from './IconTooltipCell';
import { useTranslation } from 'react-i18next';
import { StoreItemProps } from './StoreProductsTable.types';

const StoreItemTable = (props: StoreItemProps) => {
  const { t } = useTranslation();
  const { store, editable, products, loadingData, onSaveProduct, userType } = props;

  const [data, setData] = useState(products);
  const [originalData, setOriginalData] = useState(products);

  const [validRows, setValidRows] = useState({});

  const [cancelEdition, setCancelEdition] = useState({});
  const [editedRows, setEditedRows] = useState({});

  const columns = [
    {
      header: t('menuDetails.storeItem.table.item'),
      cell: IconTooltipCell,
      enableSorting: false,
    } as any,
    { header: t('menuDetails.storeItem.table.description'), accessorKey: 'description', enableSorting: false } as any,
    {
      header: t('menuDetails.storeItem.table.price'),
      accessorKey: 'chosenPrice',
      enableSorting: false,
      cell: CellEditProductPrice,
      meta: {
        validate: (newValue: number, product: Product) => {
          const percentage = product.range || 0;

          const limSup = (product.basePrice + percentage * product.basePrice).toFixed(2);
          const limInf = (product.basePrice - percentage * product.basePrice).toFixed(2);
          return newValue <= Number(limSup) && newValue >= Number(limInf);
        },
      },
    } as any,
  ];
  if (editable) {
    columns.push({ header: 'EDIT', accessorKey: '', cell: CellEditProduct } as any);
  }

  useEffect(() => {
    setData(products);
  }, [products]);

  return (
    <div className={rowStyles.tablePage}>
      <div>
        {(products.length > 0 || loadingData) && (
          <Box mb={3} key={store.storeNumber}>
            <TableCard
              title={t('menuDetails.storeItem.cardTitle', { storeNumber: store.storeNumber })}
              includeMinWidth={false}
              returnedRecordsText={(count) => t('menu.table.returnedRecord', { count })}
              isLoading={loadingData}
              itemIdentifier="variantCode"
              showPagination
              data={products}
              columns={columns}
              meta={{
                data,
                columns, // just to make the compiler happy
                editedRows,
                setEditedRows,
                cancelEdition,
                setCancelEdition,
                validRows,
                storeNumber: store.storeNumber,
                onSaveItemData: onSaveProduct,
                setValidRows,
                loadingData,
                revertData: (rowIndex: number, revert: boolean) => {
                  if (revert) {
                    setData((old) => old.map((row, index) => (index === rowIndex ? originalData[rowIndex] : row)));
                  } else {
                    setOriginalData((old) => old.map((row, index) => (index === rowIndex ? data[rowIndex] : row)));
                  }
                },
                updateData: (rowIndex: number, columnId: string, value: string | boolean) => {
                  setData((old) =>
                    old.map((row, index) => {
                      if (index === rowIndex) {
                        return {
                          ...old[rowIndex],
                          [columnId]: value,
                        };
                      }
                      return row;
                    }),
                  );
                  // Mark the edited cell as valid (true) so the submit button isn't incorrectly disabled.
                  // Previously this was set to an empty string which is falsy and caused pointer-events: none on the Submit button.
                  setValidRows((old: Record<string, unknown>[]) => ({
                    ...old,
                    [rowIndex]: { ...old[rowIndex], [columnId]: true },
                  }));
                },
                userType,
              }}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default StoreItemTable;
